import { areaToString, distanceToString } from '@/utils/util'
import * as turf from '@turf/turf'

export class MeasureTools {
  $cesiumPlus = undefined
  entitiesList = []
  analyzeEntities = []

  constructor (cesiumPlus) {
    this.$cesiumPlus = cesiumPlus
    this.entitiesList = []
    this.analyzeEntities = []
  }

  cancel () {
    this.$cesiumPlus.drawHandler.quitDraw()
    // this.clearAllEntity()
  }

  // 距离测量
  distance () {
    this.$cesiumPlus.drawHandler.draw(CesiumPlus.DrawMode.Polyline, {
      clampMode: CesiumPlus.ClampMode.Space,
      drawUpdateCall: (position, entity) => {
        const positions = entity.polyline.positions.getValue(Cesium.JulianDate.now())
        let distance = 0
        for (let i = 0; i < positions.length - 1; i++) {
          const dis = Cesium.Cartesian3.distance(positions[i], positions[i + 1])
          distance += dis
        }
        const text = distanceToString(distance)
        this.createMeasureLabel(entity, positions[positions.length - 1], text)
      },
      drawEndCall: (position, entity) => {
        this.createCloseBillboard(entity)
        this.entitiesList.push(entity.id)
      },
      styleList: [CesiumPlus.PolylineStyle.Measure()],
      properties: {
        labelAlwayShow: true,
        measureClose: true
      }
    })
  }

  // 面积测量
  area () {
    this.$cesiumPlus.drawHandler.draw(CesiumPlus.DrawMode.Polygon, {
      clampMode: CesiumPlus.ClampMode.Space,
      drawUpdateCall: (position, entity) => {
        if (!entity.polygon.hierarchy || entity.polygon.hierarchy.getValue(Cesium.JulianDate.now()).positions.length < 3) {
          return
        }
        const geoJson = this.$cesiumPlus.utils.entityExportGeoJson(entity)
        const positions = entity.polyline.positions.getValue(Cesium.JulianDate.now())
        const area = turf.area(JSON.parse(geoJson))
        const text = areaToString(area)
        this.createMeasureLabel(entity, positions[positions.length - 1], text)
      },
      drawEndCall: (position, entity) => {
        this.createCloseBillboard(entity)
        this.entitiesList.push(entity.id)
      },
      styleList: [CesiumPlus.PolygonStyle.Measure()],
      properties: {
        labelAlwayShow: true,
        measureClose: true
      }
    })
  }

  // 视野分析
  cameraView () {
    this.$cesiumPlus.drawHandler.draw(CesiumPlus.DrawMode.Segment, {
      clampMode: 2,
      drawEndCall: (position, entity) => {
        const positions = entity.polyline.positions.getValue(Cesium.JulianDate.now())
        if (positions.length < 2) {
          return
        }
        this.$cesiumPlus.cameraManager.setView({
          originCartesian3: positions[0],
          desCartesian3: positions[1]
        })
        this.$cesiumPlus.cameraManager.enableFirstPersion = true
        this.$cesiumPlus.viewer.entities.remove(entity)
        this.$cesiumPlus.vueView.$message.success('按Esc键退出视野分析模式')
      },
      styleList: [
        new CesiumPlus.PolylineStyle({
          material: new Cesium.PolylineArrowMaterialProperty(),
          width: 10
        })
      ]
    })
  }

  // 通视分析
  visibilityAnalysis () {
    this.clearAnalyzeEntities()
    this.$cesiumPlus.drawHandler.draw(CesiumPlus.DrawMode.Segment, {
      clampMode: CesiumPlus.ClampMode.Space,
      drawEndCall: (position, entity) => {
        const positions = entity.polyline.positions.getValue(Cesium.JulianDate.now())
        const car = this.$cesiumPlus.utils.cartesianToLonLat(positions[0])
        const fixPos0 = Cesium.Cartesian3.fromDegrees(car.longitude, car.latitude, car.height + 0.1)

        let direction = Cesium.Cartesian3.subtract(positions[1], fixPos0, new Cesium.Cartesian3())
        direction = Cesium.Cartesian3.normalize(direction, direction)
        const ray = new Cesium.Ray(fixPos0, direction)
        // let intersection = this.viewer.scene.globe.pick( ray, this.viewer.scene);
        const pickObject = this.$cesiumPlus.viewer.scene.pickFromRay(ray)
        if (pickObject === undefined || pickObject.position === undefined) {
          // intersection = startPoint.clone();
          console.log('pick position undefined')
          this.drawLine(positions[0], positions[1], this.invisibleColor)
          return
        }
        const intersection = pickObject.position
        const dir1 = Cesium.Cartesian3.normalize(Cesium.Cartesian3.subtract(intersection, positions[0], new Cesium.Cartesian3()), new Cesium.Cartesian3())
        const dir2 = Cesium.Cartesian3.normalize(Cesium.Cartesian3.subtract(positions[1], intersection, new Cesium.Cartesian3()), new Cesium.Cartesian3())
        const angle = Cesium.Cartesian3.angleBetween(dir1, dir2)
        if (angle > 3) {
          this.drawLine(positions[0], positions[1], Cesium.Color.GREEN, '1')
        } else {
          this.drawLine(positions[0], intersection, Cesium.Color.GREEN, '1')
          this.drawLine(intersection, positions[1], Cesium.Color.RED, '2')
        }
        this.$cesiumPlus.markerManager.removeMarkerById(entity.id)
        // this.$nextTick(() => {
        //   this.$store.commit('SET_DRAWSTATE', true)
        // })
      },
      styleList: [CesiumPlus.PolylineStyle.Measure()],
      properties: {
        labelAlwayShow: true,
        measureClose: true
      }
    })
  }

  // 体积测量
  volumeMeasure () {
    this.$cesiumPlus.drawHandler.draw(CesiumPlus.DrawMode.Polygon, {
      clampMode: CesiumPlus.ClampMode.GroundAndModel,
      drawEndCall: async (position, entity) => {
        const marker = await this.$cesiumPlus.markerManager.createMarker({
          layerId: 'volume',
          entity: entity
        })
        this.$cesiumPlus.vueView.$store.commit('showVolumeMeasureBox', marker)
        this.entitiesList.push(entity.id)
      },
      styleList: [CesiumPlus.PolygonStyle.Measure()],
      properties: {
        labelAlwayShow: true,
        measureClose: true
      }
    })
  }

  // 可视域分析
  viewableAnalysis () {
    this.$cesiumPlus.drawHandler.draw(CesiumPlus.DrawMode.Segment, {
      clampMode: CesiumPlus.ClampMode.Space,
      drawUpdateCall: (position, entity) => {
        const positions = entity.polyline.positions.getValue(Cesium.JulianDate.now())
        if (positions.length < 2) {
          return
        }
        this.$cesiumPlus.viewShed3D.analyze({
          viewPosition: positions[0],
          viewPositionEnd: positions[1]
        })
        entity.polyline.show = false
      },
      drawEndCall: (position, entity) => {
        const positions = entity.polyline.positions.getValue(Cesium.JulianDate.now())
        this.createMeasureLabel(entity, positions[0])
        this.createCloseBillboard(entity)

        this.$cesiumPlus.vueView.$store.commit('SELECT_VIEWSHED3DDATA', {
          horizontalViewAngle: this.$cesiumPlus.viewShed3D._horizontalViewAngle,
          verticalViewAngle: this.$cesiumPlus.viewShed3D._verticalViewAngle,
          viewDistance: this.$cesiumPlus.viewShed3D._viewDistance,
          viewHeading: this.$cesiumPlus.viewShed3D._viewHeading,
          viewPitch: this.$cesiumPlus.viewShed3D._viewPitch,
          alpha: this.$cesiumPlus.viewShed3D._alpha
        })
        // this.$nextTick(() => {
        //   this.$store.commit('SET_DRAWSTATE', true)
        // })
        this.entitiesList.push(entity.id)
      },
      escCall: () => {
        // 清除可视域分析
        this.$cesiumPlus.viewShed3D.clear()
        this.$cesiumPlus.viewShed3D.remove()
        // 关闭可视域分析弹窗
        // this.$store.commit('SET_DRAWSTATE', false)
      },
      styleList: [
        new CesiumPlus.PolylineStyle({
          material: new Cesium.PolylineArrowMaterialProperty(),
          width: 10
        })
      ],
      properties: {
        labelAlwayShow: true,
        measureClose: true,
        isviewShed3D: true
      }
    })
  }

  cancelVolumeMeasure () {
    this.$cesiumPlus.drawHandler.quitDraw()
    this.$cesiumPlus.vueView.$store.commit('showVolumeMeasureBox', null)
  }

  cancelViewableAnalysis () {
    this.$cesiumPlus.drawHandler.quitDraw()
    this.clearAllEntity()
    this.$cesiumPlus.vueView.$store.commit('SELECT_VIEWSHED3DDATA', null)
  }

  drawLine (startPoint, endPoint, material, type) {
    const entity = this.$cesiumPlus.viewer.entities.add({
      polyline:
        {
          positions: [startPoint, endPoint],
          width: 3.0,
          material: material
          // depthFailMaterial: material
        }
    })
    this.analyzeEntities.push(entity)
    const text = '观察位置'
    const text2 = '目标点'
    if (type === '1') {
      this.createMeasureLabel(entity, startPoint, text)
      this.createCloseBillboard(entity)
    }
    if (type === '2') {
      this.createMeasureLabel2(entity, endPoint, text2)
    }
    this.entitiesList.push(entity.id)
  }

  // 创建测量结果
  createMeasureLabel (entity, position, text) {
    entity.position = position
    if (!entity.label) {
      entity.label = new Cesium.LabelGraphics({
        text: text,
        font: '14px sans-serif',
        pixelOffset: new Cesium.Cartesian2(0, -50),
        horizontalOrigin: Cesium.HorizontalOrigin.CENTER,
        verticalOrigin: Cesium.HorizontalOrigin.CENTER,
        showBackground: false,
        fillColor: Cesium.Color.fromCssColorString('#FFFFFF'),
        outlineColor: Cesium.Color.fromCssColorString('#000000'),
        style: Cesium.LabelStyle.FILL_AND_OUTLINE,
        heightReference: Cesium.HeightReference.NONE,
        disableDepthTestDistance: Number.POSITIVE_INFINITY,
        outlineWidth: 30,
        show: true
      })
    } else {
      entity.label.text = text
    }
  }

  createMeasureLabel2 (entity, position, text) {
    entity.position = position
    entity.label = new Cesium.LabelGraphics({
      text: text,
      font: '14px sans-serif',
      pixelOffset: new Cesium.Cartesian2(0, -50),
      horizontalOrigin: Cesium.HorizontalOrigin.CENTER,
      verticalOrigin: Cesium.HorizontalOrigin.CENTER,
      showBackground: false,
      fillColor: Cesium.Color.fromCssColorString('#FFFFFF'),
      outlineColor: Cesium.Color.fromCssColorString('#000000'),
      style: Cesium.LabelStyle.FILL_AND_OUTLINE,
      heightReference: Cesium.HeightReference.NONE,
      disableDepthTestDistance: Number.POSITIVE_INFINITY,
      outlineWidth: 30,
      show: true
    })
  }

  createCloseBillboard (entity) {
    if (!entity.billboard) {
      entity.billboard = new Cesium.BillboardGraphics({
        image: require('../assets/textures/close.png'),
        pixelOffset: new Cesium.Cartesian2(0, -10),
        heightReference: Cesium.HeightReference.NONE,
        disableDepthTestDistance: Number.POSITIVE_INFINITY
      })
    }
  }

  // 清除通视分析结果
  clearAnalyzeEntities () {
    if (this.analyzeEntities && this.analyzeEntities.length) {
      for (let i = 0; i < this.analyzeEntities.length; i++) {
        this.$cesiumPlus.viewer.entities.remove(this.analyzeEntities[i])
      }
    }
    this.analyzeEntities = []
  }

  // 清除所有测量分析结果
  clearAllEntity () {
    if (!this.$cesiumPlus) {
      return
    }
    this.entitiesList.forEach(item => {
      this.$cesiumPlus.markerManager.removeMarkerById(item)
    })
    this.entitiesList = []
    this.clearAnalyzeEntities()
  }
}
